import React, { forwardRef, useCallback, useMemo } from "react";

import { ErrorBoundary } from "@sentry/react";
import Skeleton from "antd/es/skeleton";
import cn from "classnames";
import { CellMeasurer, CellMeasurerCache, List } from "react-virtualized";
import AutoSizer from "react-virtualized-auto-sizer";
import { ScrollParams } from "react-virtualized/dist/es/Grid";
import type { ListRowProps } from "react-virtualized/dist/es/List";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import TimelineEvent, { BaseTimelineEvent } from "@app/types/events/TimelineEvent";
import { formatRawDate } from "@app/util/formatters";

import styles from "./EventList.module.scss";
import DateStartEvent from "./events/DateStartEvent";
import EventItem from "./events/EventItem";
import NoResults from "./NoResults";

type TimelineOrDateEvent =
  | (Pick<BaseTimelineEvent, "id" | "updatedAt"> & { action: "dateStart" })
  | TimelineEvent;

export interface EventListProps {
  className?: string;
  events: TimelineEvent[];
  eventsExpanded: boolean;
  loading?: boolean;
  onClearFilters?: () => void;
  onLoadMore?: () => void;
  onShowTimelinePreferences?: () => void;
  rowHeightCache: CellMeasurerCache;
}

const EventList = forwardRef<List, EventListProps>(
  (
    {
      className,
      events,
      eventsExpanded,
      loading,
      onClearFilters,
      onLoadMore,
      onShowTimelinePreferences,
      rowHeightCache,
    },
    ref
  ) => {
    // add date separators between events
    const eventsWithDayEvents: TimelineOrDateEvent[] = useMemo(() => {
      const result: TimelineOrDateEvent[] = [];
      let lastDate = "";
      events.forEach((event) => {
        const key = formatRawDate(event.updatedAt, "P");
        if (lastDate !== key) {
          result.push({ id: `date-${key}`, action: "dateStart", updatedAt: event.updatedAt });
          lastDate = key;
        }
        result.push(event);
      });

      return result;
    }, [events]);

    const handleScroll = useCallback(
      ({ clientHeight, scrollHeight, scrollTop }: ScrollParams) => {
        // threshold is 200px, but less than a half of the list height
        const threshold = Math.min(200, clientHeight / 2);

        // It would be great to check if we have loaded all events already, but
        // it's not feasible doing now when platform returns a lot of unrelated events
        if (scrollHeight - clientHeight - scrollTop < threshold && !loading && scrollTop > 0) {
          onLoadMore?.();
        }
      },
      [loading, onLoadMore]
    );

    const renderRow = useDynamicCallback(({ index, key, parent, style }: ListRowProps) => {
      return (
        <CellMeasurer
          cache={rowHeightCache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {({ measure, registerChild }) =>
            eventsWithDayEvents[index].action === "dateStart" ? (
              <DateStartEvent
                date={eventsWithDayEvents[index].updatedAt}
                // that's fine that null might be returned
                // @ts-ignore
                ref={registerChild}
                style={style}
              />
            ) : (
              <ErrorBoundary onError={console.error}>
                <EventItem
                  event={eventsWithDayEvents[index] as TimelineEvent}
                  expanded={eventsExpanded}
                  onSizeChange={measure}
                  // that's fine that null might be returned
                  // @ts-ignore
                  ref={registerChild}
                  style={style}
                />
              </ErrorBoundary>
            )
          }
        </CellMeasurer>
      );
    });

    return (
      <div className={cn(styles.container, className)}>
        {!eventsWithDayEvents.length ? (
          loading ? (
            <>
              <Skeleton active avatar paragraph={{ rows: 3 }} />
              <Skeleton active avatar paragraph={{ rows: 3 }} />
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            </>
          ) : (
            <NoResults
              onClear={onClearFilters}
              onShowTimelinePreferences={onShowTimelinePreferences}
            />
          )
        ) : (
          <AutoSizer className={styles.autosizer}>
            {({ height, width }) => (
              <List
                deferredMeasurementCache={rowHeightCache}
                height={height}
                onScroll={handleScroll}
                overscanRowCount={3}
                ref={ref}
                rowCount={eventsWithDayEvents.length}
                rowHeight={rowHeightCache.rowHeight}
                rowRenderer={renderRow}
                width={width}
              />
            )}
          </AutoSizer>
        )}
      </div>
    );
  }
);

export default EventList;
